<template>
  <div class="card mb-3">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h5 class="mb-0">Bulk Upload</h5>
      <b-button v-b-toggle.bulk-upload-collapse variant="link" size="sm" @click="toggleIcon">
        <font-awesome-icon :icon="['fal', collapseIcon]" />
      </b-button>
    </div>
    <b-collapse id="bulk-upload-collapse">
      <div class="card-body">
        <ValidationObserver v-slot="v">
          <b-form @submit.prevent="v.handleSubmit(handleSubmit)">
            <div class="mb-3">
              <p class="mb-1"><strong>File requirements:</strong></p>
              <ul class="pl-4 mb-3">
                <li>Must be of format<code>.csv</code>(Comma-separated values)</li>
                <li>
                  Have three columns named:
                  <ul class="pl-4">
                    <li><code>pay_period_start</code></li>
                    <li><code>blackout_period_start</code></li>
                    <li><code>paid_on</code></li>
                  </ul>
                </li>
                <li>Date format must be: <b>YYYY-MM-DD</b>. Example: 1993-12-24</li>
                <li>Pay period start must be <u>before</u> the paid on date.</li>
                <li>Pay period start must be <u>unique</u> throughout pay schedule.</li>
                <li>Paid on date must be <u>unique</u> throughout pay schedule.</li>
                <li>If defined, blackout period start must be <u>strictly between</u> pay period start and date paid on.</li>
              </ul>
              <p class="mb-1"><strong>Example CSV file structure</strong></p>
              <table class="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>pay_period_start</th>
                    <th>blackout_period_start</th>
                    <th>paid_on</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>2025-01-01</td>
                    <td></td>
                    <td>2025-01-25</td>
                  </tr>
                  <tr>
                    <td>2025-02-01</td>
                    <td>2025-02-20</td>
                    <td>2025-02-28</td>
                  </tr>
                  <tr>
                    <td>2025-03-01</td>
                    <td>2025-03-20</td>
                    <td>2025-03-31</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <b-row class="mb-3">
              <b-col>
                <file-pond
                  ref="filepond"
                  @addfile="onAddFile"
                  @removefile="onRemoveFile"
                  accepted-file-types="text/csv"
                  file-validate-type-label-expected-types="Expects a CSV file"
                  max-files="1"
                  label-idle="Drop CSV file here or <span class='filepond--label-action'> Browse </span>"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <page-sub-footer>
                  <template #before>
                    <sme-alert v-if="error" level="danger">{{ error }}</sme-alert>
                    <sme-alert v-if="validationErrors.length > 0" level="danger">
                      <strong>Validation errors:</strong>
                      <ul class="mb-0 mt-2">
                        <li v-for="(err, index) in validationErrors" :key="index">{{ err }}</li>
                      </ul>
                    </sme-alert>
                  </template>
                  <b-button type="submit" variant="primary" :disabled="loading || isSubmitting || !hasFile">
                    <b-spinner small v-if="loading || isSubmitting" class="mr-2" />
                    Submit
                  </b-button>
                </page-sub-footer>
              </b-col>
            </b-row>
          </b-form>
        </ValidationObserver>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { ref, reactive, computed, defineComponent, getCurrentInstance } from 'vue';
import Papa from 'papaparse';
import pick from 'lodash/pick';
import { ValidationObserver } from 'vee-validate';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';
import ApiClient from '@/ApiClient';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';

const FilePond = vueFilePond(FilePondPluginFileValidateType);
const textDecoder = new TextDecoder();

const CSV_FIELDS = ['pay_period_start', 'paid_on', 'blackout_period_start'];

export default defineComponent({
  name: 'BulkUploadPaySchedule',
  components: {
    ValidationObserver,
    FilePond,
    PageSubFooter,
    SmeAlert,
  },
  props: {
    payScheduleId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ['upload-success'],
  setup(props, { emit }) {
    const upload = reactive({
      fileContent: null,
      fileName: null,
      fileParsed: null,
      validationErrors: {},
    });

    const loading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const validationErrors = ref([]);
    const badFileName = ref(false);
    const successfullyValidated = ref(false);
    const filepond = ref(null);
    const collapseIcon = ref('chevron-right');

    const hasFile = computed(() => {
      return !!upload.fileContent;
    });

    const toggleIcon = () => {
      collapseIcon.value = collapseIcon.value === 'chevron-right' ? 'chevron-down' : 'chevron-right';
    };

    const validateCSVData = (data) => {
      const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
      const validationErrors = [];

      const isValidDate = (dateString) => {
        if (!dateFormatRegex.test(dateString)) {
          return false;
        }
        
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return false;
        }
        
        const [year, month, day] = dateString.split('-').map(Number);
        return (
          date.getUTCFullYear() === year &&
          date.getUTCMonth() + 1 === month &&
          date.getUTCDate() === day
        );
      };

      const validateDateField = (row, rowNum, fieldName, value, isRequired) => {
        const originalValue = value || '';
        const trimmedValue = originalValue.trim();
        
        // Check for leading/trailing whitespace
        if (originalValue !== trimmedValue) {
          validationErrors.push(`Row ${rowNum}: ${fieldName} must not have leading or trailing whitespace`);
          return;
        }
        
        if (isRequired && trimmedValue === '') {
          validationErrors.push(`Row ${rowNum}: ${fieldName} is required`);
          return;
        }
        
        if (trimmedValue !== '') {
          if (!dateFormatRegex.test(trimmedValue)) {
            validationErrors.push(`Row ${rowNum}: ${fieldName} must be in YYYY-MM-DD format`);
          } else if (!isValidDate(trimmedValue)) {
            validationErrors.push(`Row ${rowNum}: ${fieldName} is not a valid date`);
          }
        }
      };

      data.forEach((row, index) => {
        const rowNum = index + 1;
        validateDateField(row, rowNum, 'pay_period_start', row.pay_period_start, true);
        validateDateField(row, rowNum, 'paid_on', row.paid_on, true);
        validateDateField(row, rowNum, 'blackout_period_start', row.blackout_period_start, false);
      });

      return validationErrors;
    };

    const handleSubmit = async () => {
      if (!upload.fileContent) {
        error.value = 'File is missing';
        return;
      }

      try {
        isSubmitting.value = true;
        loading.value = true;
        error.value = null;
        validationErrors.value = [];

        const fileAsString = textDecoder.decode(upload.fileContent).replace(/^\uFEFF/, '');
        const { data, meta } = await new Promise((resolve, reject) => {
          Papa.parse(fileAsString, {
            header: true,
            skipEmptyLines: true,
            error: error => reject(error),
            complete: results => resolve(results),
          });
        });

        if (!meta.fields || !CSV_FIELDS.every(field => meta.fields?.includes(field))) {
          const missingCols = CSV_FIELDS.filter(f => !meta.fields?.includes(f));
          error.value = `File is missing required columns: ${missingCols.join(', ')}`;
          return;
        }

        const errors = validateCSVData(data);
        if (errors.length > 0) {
          validationErrors.value = errors;
          error.value = null;
          return;
        }
        validationErrors.value = [];

        const payload = data.map(entry => ({
          ...pick(entry, CSV_FIELDS),
        }));

        const uploadResponseResult = await ApiClient.postPayScheduleBulkUpload(props.payScheduleId, payload);
        if (!uploadResponseResult.ok) {
          error.value = "Incorrect pay schedules data. Please refer to the guidelines above."
          return;
        }
        // Using the global app instance for toast
        const instance = getCurrentInstance();
        if (instance?.proxy?.$appToast) {
          instance.proxy.$appToast('Successfully uploaded pay schedules', { title: 'Success', variant: 'success' });
        }
        // Clear the file upload
        if (filepond.value) {
          filepond.value.removeFiles();
        }
        emit('upload-success');
      } catch (err) {
        console.error('Upload error:', err);
        error.value = err.message || 'Failed to process or upload file.';
      } finally {
        loading.value = false;
        isSubmitting.value = false;
      }
    };

    const onAddFile = (error, fileItem) => {
      if (error) {
        upload.fileContent = null;
        upload.fileName = null;
        return;
      }

      if (!fileItem || !fileItem.file) {
        error.value = 'Invalid file';
        return;
      }

      const fileReader = new FileReader();
      upload.fileName = fileItem.file.name;
      fileReader.onloadend = handleFileRead;
      fileReader.readAsArrayBuffer(fileItem.file);
    };

    const handleFileRead = e => {
      upload.fileContent = new Uint8Array(e.target.result);
      const fileAsString = textDecoder.decode(upload.fileContent).replace(/^\uFEFF/, '');
      Papa.parse(fileAsString, {
        header: true,
        complete: results => {
          upload.fileParsed = results;
          updateValidationErrors();
        },
        error: errors => {
          upload.fileParsed = errors;
        },
      });
    };

    const onRemoveFile = () => {
      upload.fileContent = null;
      upload.validationErrors = {};
      upload.fileName = null;
      badFileName.value = false;
      successfullyValidated.value = false;
      error.value = null;
      validationErrors.value = [];
    };

    const updateValidationErrors = () => {
      if (!upload.fileParsed || !upload.fileParsed.meta) {
        return;
      }

      const { fields } = upload.fileParsed.meta;
      if (!fields || !CSV_FIELDS.every(field => fields.includes(field))) {
        const missingCols = CSV_FIELDS.filter(f => !fields.includes(f));
        upload.validationErrors.missing = missingCols;
      }
    };

    return {
      upload,
      loading,
      isSubmitting,
      error,
      validationErrors,
      badFileName,
      successfullyValidated,
      filepond,
      hasFile,
      handleSubmit,
      onAddFile,
      handleFileRead,
      onRemoveFile,
      updateValidationErrors,
      collapseIcon,
      toggleIcon,
    };
  },
});
</script>

<style lang="scss">
.filepond--drop-label {
  border: 2px dashed var(--color-primary);
  border-radius: 0.5em;
  box-sizing: border-box;
  cursor: pointer;
  label {
    cursor: pointer;
  }
}

.filepond--wrapper {
  margin-bottom: 2rem !important;
  margin-top: 2rem !important;
}
</style>
